<template>
    <div>
        <v-skeleton-loader
            v-show="loading"
            ref="skeleton"
            :type="'table'"
            class="mx-auto"
        ></v-skeleton-loader>
        <v-data-table
            v-show="!loading"
            :headers="headers"
            :items="items"
            :sort-by.sync="sortBy"
            :sort-desc.sync="descending"
            :server-items-length="total"
            :loading="loading"
            must-sort
            :multi-sort="false"
            hide-default-footer
            dense
            :show-expand="showExpand"
            :single-expand="singleExpand"
            :expand-icon="expandIcon"
            :expanded.sync="expanded"
            :item-class="rowColor"
            @click:row="itemClick"
            @dblclick:row="rowDbClick"
        >


            <!-- Dynamic header slots -->
            <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item }">
                <slot
                    :name="`item.${header.value}`"
                    :item="item"
                >
                    {{ item[header.value] }}
                </slot>
            </template>

            <template v-slot:[`item.action`]="{ item }">
                <v-menu bottom right>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            dark
                            icon
                            color="blue darken-2"
                            v-on="on"
                            @click="itemClick(item)"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>

                    <v-list nav dense>
                        <template v-for="(action, index) in actions">
                            <v-list-item
                                :key="`action-${index}`"
                                @click="action.func(item)"
                                v-if="action.access(item)"
                                :disabled="action.disabled(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon :color="action.color">{{ action.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{ action.title }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
                <slot
                    name="expanded-item"
                    :item="item"
                    :headers="headers"
                >
                </slot>
            </template>

            <!-- Footer slot -->
            <template v-slot:footer="{}">
                <slot name="footer">
                    <br/>
                    <div class="text-center">
                        <v-row justify="center">
                            <v-col md="6">
                                <v-pagination
                                    v-model="page"
                                    :length="Math.ceil(total / itemsPerPage)"
                                    total-visible="15"
                                    @input="updateSearchParams"
                                />
                            </v-col>
                            <v-col md="2">
                                <w-enum-select
                                    v-model="itemsPerPage"
                                    :items="paginationListLength"
                                    :label="$t('common.items_per_page')"
                                    @change="updateSearchParams"
                                />
                            </v-col>
                        </v-row>
                    </div>
                </slot>
            </template>
        </v-data-table>

        <input type="hidden">
    </div>
</template>

<script>

import useSharedData from "@/use/SharedData";
import {ref, watch} from "vue";

export default {
    name: "WServerDataTable",
    props: {
        value: Object,
        headers: Array,
        items: Array,
        actions: Array,
        total: Number,
        loading: Boolean,
        selectFirst: Boolean,
        showDefaultItemColor: Boolean,
        showExpand: Boolean,
        showAllExpanded: Boolean,
        singleExpand: Boolean,
        expandIcon: String,
    },
    emits: ["rowDbClick", "itemClick", "input"],
    setup(props, {emit}) {
        const {
            page,
            itemsPerPage,
            paginationListLength,
            itemColor
        } = useSharedData();

        const expanded = ref([]);

        const selectedOne = ref({id: null})

        const sortBy = ref(props.value.sortBy);
        const descending = ref(props.value.descending);

        page.value = props.value.page;
        itemsPerPage.value = props.value.itemsPerPage;

        function updateSearchParams() {
            const searchParams = {
                sortBy: sortBy.value,
                descending: descending.value,
                page: page.value,
                itemsPerPage: itemsPerPage.value
            };

            // Emit the updated searchParams as v-model
            emit('input', searchParams);
        }

        function rowDbClick(event, {item}) {
            emit("rowDbClick", item);
        }

        function rowColor(item) {
            if (item.id === selectedOne.value.id) {
                return itemColor.value;
            }

            if (props.showDefaultItemColor)
                return "brown lighten-5";
        }

        function itemClick(item) {
            selectedOne.value = item;
            emit('itemClick', item);
        }

        watch([sortBy, descending], () => {
            updateSearchParams();
        });

        let firstOne = {};
        watch(() => props.items, () => {
            if (props.showAllExpanded)
                expanded.value = props.items;
            if (props.selectFirst) {
                const [firstItem] = props.items;
                if (firstItem?.id !== firstOne.id) { // Optional chaining for safety
                    firstOne = firstItem ?? {id: null}; // Fallback to `{ id: null }` if undefined
                    itemClick(firstOne);
                }
            }
        }, {deep: true, immediate: true});


        watch(() => props.value, (newValue) => {
            sortBy.value = newValue.sortBy;
            descending.value = newValue.descending;
            page.value = newValue.page;
            itemsPerPage.value = newValue.itemsPerPage;
        }, {deep: true});

        return {
            page,
            itemsPerPage,
            paginationListLength,
            sortBy,
            descending,
            expanded,
            updateSearchParams,
            rowColor,
            itemClick,
            rowDbClick,
        }
    }
}
</script>

<style scoped>

</style>